import { Box } from "../../components/ui/box";
import { Icon } from "../../components/ui/icon";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Separator } from "../../components/ui/separator";
import { Text } from "../../components/ui/text";
import * as React from "react";
import {
  isItemCollapsed,
  subsectionKey,
  useEntitySelector,
  type EntityItemConfig,
} from "./entity-selector-context";
import { EntitySelectorItem } from "./entity-selector-item";
import type { Omittable } from "../../lib/props";

const ITEM_HEIGHT = 44;

// Virtual mode renders the loaded prefix plus at most this many placeholders so
// the list grows progressively instead of mounting all `total` skeleton rows up front.
const PLACEHOLDER_OVERSCAN = 20;

export type EntitySelectorPoolProps = Omittable<{
  value: string;
  label?: string;
  onItemAction?: (entity: EntityItemConfig) => void;
  hideSelectionHighlight?: boolean;
}>;

type EntitySectionHeadingProps = Omittable<{
  label: string;
  collapsed?: boolean;
  onToggle?: () => void;
  /** A `subsection` heading — indented under its section and cased naturally
   *  (rather than upper) so the two levels are distinguishable at a glance. */
  nested?: boolean;
}>;

// The eyebrow's own content, shared by the static and the pressable spelling so
// the two cannot drift apart.
//
// It carries NO item count. A count answers "how much is hidden behind this
// heading", which only matters if a fold is the normal resting state — and it
// is not: sections start open, so the number is redundant beside the rows it is
// counting, and it competes with the label for the eye on every heading in the
// list to serve the collapsed minority.
function EntitySectionHeadingContent({
  label,
  collapsed,
  onToggle,
  nested = false,
}: EntitySectionHeadingProps): React.ReactElement {
  return (
    <>
      {onToggle ? (
        <Icon
          color="muted"
          name={collapsed ? "chevron-right" : "chevron-down"}
          size="xs"
        />
      ) : null}
      <Text size="xs" variant="muted" weight="semibold">
        {nested ? label : label.toUpperCase()}
      </Text>
    </>
  );
}

// In-list eyebrow above a run of same-`section` items. Smaller and quieter
// than the pool-label header (xs semibold muted, uppercased here so callers
// pass natural case) — it subdivides one list rather than titling it.
//
// With `onToggle` it becomes a pressable disclosure: a real `button`, so it is
// focusable and answers Enter/Space with no extra handlers, carrying
// `aria-expanded` and a leading chevron that follows `collapsed`. The whole row
// is the hit target. Without one it renders a plain static eyebrow.
function EntitySectionHeading(
  props: EntitySectionHeadingProps,
): React.ReactElement {
  const { collapsed, onToggle, nested = false } = props;
  // Even padding above and below. An eyebrow with 8px over it and 2px under it
  // sits nearer the rows it labels than the group it separates from, which is
  // backwards — a heading belongs to what follows it, but it still has to look
  // detached from what came before.
  const padding = {
    paddingX: "sm",
    paddingLeft: nested ? "md" : undefined,
    paddingTop: nested ? "xs" : "sm",
    paddingBottom: nested ? "2xs" : "sm",
  } as const;

  if (!onToggle) {
    return (
      <Box align="center" direction="row" gap="xs" {...padding}>
        <EntitySectionHeadingContent {...props} />
      </Box>
    );
  }

  return (
    <Box
      align="center"
      aria-expanded={!collapsed}
      as="button"
      cursor="pointer"
      direction="row"
      gap="xs"
      justify="start"
      onClick={onToggle}
      type="button"
      width="100%"
      {...padding}
    >
      <EntitySectionHeadingContent {...props} />
    </Box>
  );
}

// The collapse controls a collapsible pool threads into its headings. Keys are
// the section label, or `subsectionKey(section, subsection)` for the nested level.
type CollapseApi = {
  isCollapsed: (key: string) => boolean;
  toggle: (key: string) => void;
};

// The eyebrow heading(s) that precede row `index`: the section heading when the
// section changes here, and the subsection heading when the subsection changes
// (both fire on the first row of a new section that also has a subsection).
// Returns null for every other row. `collapse` is present only for a collapsible
// pool and makes each heading a disclosure.
function sectionHeadingFor(
  data: readonly (EntityItemConfig | null)[],
  index: number,
  collapse?: CollapseApi,
): React.ReactNode {
  const item = data[index];
  if (!item?.section) return null;
  const prev = data[index - 1];
  const section = item.section;
  const startsSection = section !== prev?.section;
  const sub = item.subsection;
  const startsSub = !!sub && (startsSection || sub !== prev?.subsection);
  if (!startsSection && !startsSub) return null;

  // A collapsed SECTION swallows its subsection headings too — only its own
  // heading stays, as the control that reopens it.
  const sectionClosed = !!collapse?.isCollapsed(section);
  return (
    <>
      {/* A rule BETWEEN groups, never above the first one. Padding alone stopped
          reading as a group boundary once several sections stacked up: the eye
          needs a line to see where one group ends, not just more air. */}
      {startsSection && index > 0 ? (
        <Separator className="-mx-1 my-1.5" />
      ) : null}
      {startsSection ? (
        collapse ? (
          <EntitySectionHeading
            collapsed={sectionClosed}
            label={section}
            onToggle={() => collapse.toggle(section)}
          />
        ) : (
          <EntitySectionHeading label={section} />
        )
      ) : null}
      {startsSub && !sectionClosed ? (
        collapse ? (
          <EntitySectionHeading
            collapsed={collapse.isCollapsed(subsectionKey(section, sub))}
            label={sub}
            nested
            onToggle={() => collapse.toggle(subsectionKey(section, sub))}
          />
        ) : (
          <EntitySectionHeading label={sub} nested />
        )
      ) : null}
    </>
  );
}

function PlaceholderRow() {
  return (
    <Box
      direction="row"
      align="center"
      gap="sm"
      paddingX="sm"
      className="py-1.5"
      height={ITEM_HEIGHT}
    >
      {/* Skeleton bars are decorative fills, not surfaces — a 24px dot has
          no elevation to carry. */}
      <Box rounded="full" background="muted" width={24} height={24} />
      <Box rounded="sm" background="muted" width={96} height={12} />
    </Box>
  );
}

function EntitySelectorPool({
  value,
  label,
  onItemAction,
  hideSelectionHighlight = false,
}: EntitySelectorPoolProps) {
  const {
    variant,
    activePool,
    getPool,
    getPoolBadge,
    getFilteredItems,
    getPinnedItems,
    isSectionCollapsed,
    toggleSection,
    search,
    activeId,
    setActiveIndex,
    isActiveFromPointer,
  } = useEntitySelector();

  const pool = getPool(value);
  const badge = getPoolBadge(value);
  const filteredItems = getFilteredItems(value);
  const sectionLabel = label ?? pool?.label;
  const isActive = activePool === value;
  const hideAvatars = pool?.hideAvatars ?? false;

  // Pinned rows sit above the section header, outside the (possibly virtual)
  // list below. Sourced from the root's cache — the same list backs keyboard
  // navigation, where pinned rows occupy indices 0..n-1 ahead of the
  // regular items.
  const pinnedItems = getPinnedItems(value);

  // Drawer mode: the DrawerPanel's ScrollArea is the ONE scrollable, so the
  // pool renders its rows flat and hooks its scroll bookkeeping onto that
  // ancestor viewport instead of mounting its own ScrollArea.
  const ownScroller = variant !== "drawer";

  // The rows container. The actual scrolling element is resolved from it:
  // the pool's own ScrollArea viewport (popover/inline) or the enclosing
  // DrawerPanel viewport (drawer).
  const rowsRef = React.useRef<HTMLDivElement | null>(null);
  const [scroller, setScroller] = React.useState<HTMLElement | null>(null);

  const rowsCallbackRef = React.useCallback((node: HTMLDivElement | null) => {
    rowsRef.current = node;
    setScroller(
      node?.closest<HTMLElement>("[data-slot=scroll-area-viewport]") ?? null,
    );
  }, []);

  const isVirtual = pool?.total != null && pool.total > 0;
  const isSearching = search.trim().length > 0;

  // Virtual mode (no search): loaded prefix plus placeholders. Otherwise: dense filtered items.
  const data = React.useMemo(() => {
    if (isVirtual && !isSearching) {
      const total = pool!.total!;
      const count = Math.min(total, filteredItems.length + PLACEHOLDER_OVERSCAN);
      const arr: (EntityItemConfig | null)[] = new Array(count).fill(null);
      // Items are assumed ordered — place them starting at index 0
      for (const [i, item] of filteredItems.entries()) {
        if (i >= count) break;
        arr[i] = item;
      }
      return arr;
    }
    return filteredItems;
  }, [isVirtual, isSearching, pool, filteredItems]);

  // ── Collapsible sections ────────────────────────────────────────────────
  // The state lives in the ROOT (see `isSectionCollapsed` on the context): a
  // folded section's rows are dropped from the keyboard-nav list, so the same
  // owner has to answer both "is this rendered" and "can the cursor reach it".
  // This binds that per-pool API to this pool's id.
  const collapseApi = React.useMemo<CollapseApi | undefined>(
    () =>
      pool?.collapsibleSections
        ? {
            isCollapsed: (key: string) => isSectionCollapsed(value, key),
            toggle: (key: string) => toggleSection(value, key),
          }
        : undefined,
    [pool?.collapsibleSections, value, isSectionCollapsed, toggleSection],
  );

  const onLoadRangeRef = React.useRef(pool?.onLoadRange);
  onLoadRangeRef.current = pool?.onLoadRange;

  // Attach scroll listener for virtual loading
  React.useEffect(() => {
    const el = scroller;
    if (!el) return;
    const handler = () => {
      if (!onLoadRangeRef.current) return;
      // Rows are NOT a fixed height: real items render at their natural height
      // (~26px) while skeletons are ITEM_HEIGHT (44px), and the list is not
      // windowed — every entry is a real DOM node packed from the top. Dividing
      // scrollTop by ITEM_HEIGHT therefore undershot the true index badly (at the
      // bottom it reported ~row 132 of 220), so onLoadRange kept asking for pages
      // the user had already scrolled past and never requested the pages for the
      // rows actually in view — the trailing rows stayed skeletons forever.
      // Derive the visible range from the real scroll geometry instead: the
      // fraction of total content scrolled maps directly to item indices,
      // regardless of per-row height.
      const { scrollTop, clientHeight, scrollHeight } = el;
      if (scrollHeight <= 0) return;
      const startIndex = Math.floor((scrollTop / scrollHeight) * data.length);
      const endIndex = Math.min(
        Math.ceil(((scrollTop + clientHeight) / scrollHeight) * data.length),
        data.length - 1,
      );
      onLoadRangeRef.current(startIndex, endIndex);
    };
    el.addEventListener("scroll", handler, { passive: true });
    return () => el.removeEventListener("scroll", handler);
  }, [scroller, data.length]);

  // Keep the active row in view for KEYBOARD navigation only. Hovering must not
  // scroll: the row under the cursor is already visible, so pulling it "fully"
  // into view just slides the list, which lands a different row under a
  // stationary pointer — and that row may itself be clipped, cascading. It bit
  // hardest on the row clipped at the top edge, which is the first thing a user
  // reaches for after opening a list that starts scrolled to its selection.
  React.useEffect(() => {
    if (!activeId || isActiveFromPointer()) return;
    const el = rowsRef.current?.querySelector(`[data-entity-id="${activeId}"]`);
    (el as HTMLElement | null)?.scrollIntoView({ block: "nearest" });
  }, [activeId, isActiveFromPointer]);

  // Items carrying `section` render their own in-list eyebrow headings, which
  // replace the single pool-label header — and set the roomier row rhythm, since
  // rows packed as tightly as the eyebrows above them stop reading as groups.
  const hasSections = data.some((item) => item?.section);

  // A row's index in the KEYBOARD-NAV list, which the root builds from the
  // pinned rows followed by the visible ones. A row under a folded heading is
  // absent from it, so `index` cannot be the row's position in `data` — that
  // spelling drifted by one per hidden row and pointed hover at the wrong row.
  const navIndexById = React.useMemo(() => {
    const byId = new Map<string, number>();
    let next = pinnedItems.length;
    for (const item of data) {
      if (!item || isItemCollapsed(item, (key) => !!collapseApi?.isCollapsed(key)))
        continue;
      byId.set(item.id, next);
      next += 1;
    }
    return byId;
  }, [data, pinnedItems.length, collapseApi]);

  const renderRow = React.useCallback(
    (item: EntityItemConfig | null, index: number) => {
      if (!item || item.id === "__placeholder__") {
        return <PlaceholderRow />;
      }
      return (
        <EntitySelectorItem
          entity={item}
          poolId={value}
          actionLabel={badge?.label}
          onAction={onItemAction}
          hideSelectionHighlight={hideSelectionHighlight}
          hideAvatar={hideAvatars}
          spacious={hasSections}
          active={item.id === activeId}
          index={navIndexById.get(item.id) ?? pinnedItems.length + index}
          onActivate={setActiveIndex}
        />
      );
    },
    [
      value,
      badge?.label,
      onItemAction,
      hideSelectionHighlight,
      hideAvatars,
      activeId,
      setActiveIndex,
      pinnedItems.length,
      navIndexById,
      hasSections,
    ],
  );

  if (!isActive || !pool) {
    return null;
  }

  const SectionHeader =
    sectionLabel && !hasSections ? (
      <Box paddingX="sm" className="py-1.5">
        <Text size="xs" weight="medium" variant="muted">
          {sectionLabel}
        </Text>
      </Box>
    ) : null;

  const PinnedRows = pinnedItems.length ? (
    <Box direction="column">
      {pinnedItems.map((item, index) => (
        <EntitySelectorItem
          key={item.id}
          entity={item}
          poolId={value}
          hideSelectionHighlight={hideSelectionHighlight}
          spacious={hasSections}
          active={item.id === activeId}
          index={index}
          onActivate={setActiveIndex}
        />
      ))}
    </Box>
  ) : null;

  if (data.length === 0) {
    // A label heading an empty list is worse than no label: it claims a section
    // exists and then shows nothing, which is what the empty state is for. It
    // stays only while loading, where it heads rows that are on their way, or
    // above pinned rows that survived the filter.
    const headed = pinnedItems.length > 0 || pool.loading === true;
    if (!headed) return null;
    return (
      <Box role="listbox" id="entity-selector-listbox" aria-label={sectionLabel}>
        {PinnedRows}
        {SectionHeader}
      </Box>
    );
  }

  const rows = (
    <Box direction="column" ref={rowsCallbackRef}>
      {data.map((item, index) => {
        // Hide the ROWS of a folded section or subsection; the headings
        // themselves still render (they are the controls that reopen). Same
        // predicate the root filters the nav list with, so what is on screen and
        // what the cursor can reach cannot disagree.
        const hidden = isItemCollapsed(item, (key) => !!collapseApi?.isCollapsed(key));
        return (
          <React.Fragment key={item?.id ?? `placeholder-${index}`}>
            {sectionHeadingFor(data, index, collapseApi)}
            {hidden ? null : (
              // Subsection rows sit indented under their sub-eyebrow so the
              // two levels read as a tree rather than a flat run.
              <Box paddingLeft={item?.subsection ? "sm" : undefined}>
                {renderRow(item, index)}
              </Box>
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );

  return (
    <Box
      role="listbox"
      id="entity-selector-listbox"
      aria-label={sectionLabel}
      flex={1}
      minHeight={0}
      direction="column"
    >
      {PinnedRows}
      {SectionHeader}

      {ownScroller ? (
        <ScrollArea
          style={{ maxHeight: Math.round(window.innerHeight * 0.65) }}
        >
          {rows}
        </ScrollArea>
      ) : (
        rows
      )}
    </Box>
  );
}

EntitySelectorPool.displayName = "EntitySelectorPool";

export { EntitySelectorPool, sectionHeadingFor };